'use client';

import { CheckCircle2, Loader2, Reply, Send, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';
import { conversationRef } from '../../lib/clinic-conversation-ref';
import { getActiveWorkspaceId, getWorkerUrl, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { UserAvatar } from '../../ui/user-avatar';
import { TiptapEditor } from '../comments/tiptap-editor';

export interface CollaborativeDraftEditorProps {
  /** The mail conversation the reply is for. The server sends it from that conversation's mailbox. */
  conversationId: number;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  isReadOnly?: boolean;
  initialActive?: boolean;
  /**
   * Whether this user may send the draft, as opposed to write it.
   *
   * Drafting and sending are separate authorities: the collaboration case is someone who
   * can compose a reply for the mailbox owner to send. Defaults true so every existing
   * caller keeps today's behaviour; the clinic portal passes the real answer.
   */
  canSend?: boolean;
}

// Presence cursors identify who is typing. These encode identity, not alarm, and have
// to stay distinguishable from each other rather than from the theme.
// calm-interface-ignore: per-user presence identity, not a UI colour decision
const CURSOR_COLORS = ['#f97316', '#22c55e', '#3b82f6', '#ec4899', '#a855f7', '#14b8a6', '#eab308'];

function colorForUser(userId: number) {
  return CURSOR_COLORS[userId % CURSOR_COLORS.length];
}

interface PresentUser {
  userId: number;
  name: string;
  color: string;
}

export function CollaborativeDraftEditor({
  conversationId,
  workspaceMembers,
  isReadOnly = false,
  initialActive = false,
  canSend = true,
}: CollaborativeDraftEditorProps) {
  const meQuery = trpc.auth.me.useQuery();
  const getOrCreate = trpc.drafts.getOrCreate.useMutation();
  const getCollabToken = trpc.drafts.getCollabToken.useMutation();
  const sendReplyMutation = trpc.drafts.sendReply.useMutation();

  const [isActivated, setIsActivated] = useState(initialActive);
  const [ydoc, setYdoc] = useState<Y.Doc | null>(null);
  const [provider, setProvider] = useState<WebsocketProvider | null>(null);
  const [status, setStatus] = useState<'draft' | 'sent'>('draft');
  const [connectionState, setConnectionState] = useState<
    'connecting' | 'connected' | 'disconnected'
  >('connecting');
  const [presentUsers, setPresentUsers] = useState<PresentUser[]>([]);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  const currentUserId = meQuery.data?.uid;
  const currentMember = currentUserId
    ? workspaceMembers.find((m) => m.id === currentUserId)
    : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialization effect only runs when activation or user context changes
  useEffect(() => {
    if (!isActivated || !currentUserId) return;

    let cancelled = false;
    let doc: Y.Doc | null = null;
    let wsProvider: WebsocketProvider | null = null;

    (async () => {
      const workspaceId = getActiveWorkspaceId();
      if (!workspaceId) return;

      try {
        const created = await getOrCreate.mutateAsync({ conversationId });
        if (cancelled) return;
        setStatus((created.status as 'draft' | 'sent') || 'draft');

        const { token } = await getCollabToken.mutateAsync({ conversationId });
        if (cancelled) return;

        doc = new Y.Doc();
        const wsBase = getWorkerUrl().replace(/^http/, 'ws');
        // The room is the conversation's link ref; the server maps it to the draft's room.
        wsProvider = new WebsocketProvider(
          `${wsBase}/ws/draft/${workspaceId}`,
          conversationRef(conversationId),
          doc,
          { params: { token } }
        );

        wsProvider.on(
          'status',
          ({ status: s }: { status: 'connected' | 'disconnected' | 'connecting' }) => {
            setConnectionState(s);
          }
        );

        wsProvider.awareness.setLocalStateField('user', {
          userId: currentUserId,
          name: currentMember?.fullName || currentMember?.email || 'Someone',
          color: colorForUser(currentUserId),
        });

        const activeDoc = doc;
        wsProvider.awareness.on('change', () => {
          if (!wsProvider || !activeDoc) return;
          const users = Array.from(wsProvider.awareness.getStates().entries())
            .filter(([clientId]) => clientId !== activeDoc.clientID)
            .map(([, state]) => (state as { user?: PresentUser }).user)
            .filter((u): u is PresentUser => !!u);
          setPresentUsers(users);
        });

        if (!cancelled) {
          setYdoc(doc);
          setProvider(wsProvider);
        }
      } catch (err) {
        console.error('[CollaborativeDraftEditor] Failed to initialize collab session:', err);
      }
    })();

    return () => {
      cancelled = true;
      wsProvider?.destroy();
      doc?.destroy();
      setYdoc(null);
      setProvider(null);
    };
  }, [isActivated, conversationId, currentUserId, currentMember?.fullName, currentMember?.email]);

  // Tab visibility management: disconnect WebSocket when tab is hidden, reconnect when visible
  useEffect(() => {
    if (!provider) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        provider.disconnect();
      } else if (document.visibilityState === 'visible') {
        provider.connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [provider]);

  const handleSend = async () => {
    try {
      await sendReplyMutation.mutateAsync({ conversationId });
      setStatus('sent');
    } catch (err) {
      console.error('[CollaborativeDraftEditor] Failed to send reply:', err);
    }
  };

  const handleCancelReply = () => {
    provider?.destroy();
    ydoc?.destroy();
    setYdoc(null);
    setProvider(null);
    setIsActivated(false);
  };

  // 1. Unactivated State: Show clean Reply trigger button
  if (!isActivated) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-muted-foreground">Reply</h4>
        </div>
        <button
          type="button"
          disabled={isReadOnly}
          onClick={() => setIsActivated(true)}
          className="w-full flex items-center gap-2.5 p-3 rounded-xl border border-border bg-muted hover:border-primary/50 hover:bg-muted text-muted-foreground hover:text-foreground text-xs text-left transition-all cursor-pointer disabled:cursor-default"
        >
          <Reply className="w-4 h-4 text-primary shrink-0" />
          <span className="font-medium">Write a reply to this thread...</span>
        </button>
      </div>
    );
  }

  // 2. Loading State: Activating Yjs session
  if (!ydoc || !provider) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-muted-foreground">Reply Draft</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelReply}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex items-center justify-center gap-2 h-20 rounded-xl border border-border bg-muted text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Starting collaborative
          session...
        </div>
      </div>
    );
  }

  const collabUser = {
    name: currentMember?.fullName || currentMember?.email || 'Someone',
    color: colorForUser(currentUserId!),
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-muted-foreground">Reply Draft</h4>
        <div className="flex items-center gap-2">
          {presentUsers.length > 0 && (
            <div className="flex -space-x-2">
              {presentUsers.map((u) => {
                const member = workspaceMembers.find((m) => m.id === u.userId);
                return (
                  <UserAvatar
                    key={u.userId}
                    user={member || { fullName: u.name }}
                    size="xs"
                    className="border-2 border-background"
                  />
                );
              })}
            </div>
          )}
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              connectionState === 'connected' ? 'bg-muted-foreground' : 'bg-muted-foreground'
            )}
            title={connectionState}
          />
        </div>
      </div>

      {status === 'sent' ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-muted p-3 text-xs font-medium text-foreground">
          <CheckCircle2 className="h-4 w-4" /> Reply sent
        </div>
      ) : (
        <>
          <TiptapEditor
            mode="draft"
            ydoc={ydoc}
            provider={provider}
            collabUser={collabUser}
            placeholder="Write a reply..."
            onChange={(html) => setIsEditorEmpty(html.trim().length === 0 || html === '<p></p>')}
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-muted-foreground hover:text-foreground"
              onClick={handleCancelReply}
            >
              Cancel
            </Button>
            {canSend ? (
              <Button
                size="sm"
                className="h-8 gap-1.5 text-xs"
                disabled={isReadOnly || isEditorEmpty || sendReplyMutation.isPending}
                onClick={handleSend}
              >
                <Send className="h-3.5 w-3.5" />
                {sendReplyMutation.isPending ? 'Sending...' : 'Send reply'}
              </Button>
            ) : (
              // Absent rather than disabled. A greyed-out Send reads as something being
              // withheld; saying where the draft goes next reads as the handover it is.
              <p className="text-xs text-muted-foreground">
                Saved for whoever owns the mailbox to send.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
